import { useEffect, useState } from 'react';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import DocumentGallery from '@product-framework/components/DocumentGallery';
import { Footer, IconLibrary, PageSection } from '@product-framework/index';
import {
  PublicBreadcrumb,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  LoadingEvent,
  Page,
  useSnackbar,
  useStore,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container } from '@mui/material';
import { observer } from 'mobx-react';
import Logo, { LOGO_TYPE } from '@product-framework/components/Logo';
import { FormattedMessage } from 'react-intl';

function PublicHelp() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const store = useStore();

  const storeName = 'HelpPagesPublic';

  const defaultStateValues = {
    paginationState: {
      pageIndex: 0,
      pageSize: 6,
    },
    filters: [],
    sortingState: [],
  };

  const {
    paginationState,
    filters = [],
    sortingState = [],
  } = store.domain.list.get(storeName, defaultStateValues);

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, get } = useDataProvider(provider);

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'LIST_PUBLIC',
        defaultMessage: 'Home',
      }),
      icon: IconLibrary.Home,
      onClick: () => navigate('/'),
    },
    {
      name: formatMessage({
        id: 'LIST_HELP',
        defaultMessage: 'Help',
      }),
      icon: IconLibrary.Help,
      onClick: () => {
        navigate('/public/help');
      },
    },
  ];

  const getHelpDocuments = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/help-document/public?limit=${
        paginationState?.pageSize
      }&page=${paginationState?.pageIndex || 1}`,
    );
    if (response.ok) {
      return response;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return [];
  };

  const loginAction = () => {
    window.location.href = '/';
  };

  const initialize = async () => {
    setLoading(true);
    const response = await getHelpDocuments();
    if (response) {
      setTotalItems(response?.data?.total);
      setDocuments(response?.data?.data);
    } else {
      setDocuments(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, [
    JSON.stringify(paginationState),
    JSON.stringify(filters),
    JSON.stringify(sortingState),
  ]);

  return (
    <Page>
      <Container>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Grid2 container>
            <Grid2 xs={6} sx={{ mt: 1 }}>
              <Logo type={LOGO_TYPE.FULL} size="medium" href="/" />
            </Grid2>
            <Grid2 xs={6} sx={{ mt: 1, textAlign: 'right' }}>
              <Button variant="contained" size="small" onClick={loginAction}>
                <FormattedMessage id="LOGIN_BUTTON" defaultMessage="Login" />
              </Button>
            </Grid2>
          </Grid2>
          <Grid2 xs={12}>
            <Box sx={{ mb: 1 }}>
              <PublicBreadcrumb items={breadCrumb} />
            </Box>
            <PageSection
              containerProps={{ sx: { mb: 3 } }}
              label={formatMessage({
                id: 'HELP_HEADING',
                defaultMessage: 'Help & Training',
              })}>
              <Box sx={{ mt: 4 }}>
                {loading ? (
                  <LoadingEvent>Fetching help documents...</LoadingEvent>
                ) : (
                  <DocumentGallery
                    showPagination
                    itemsPerRow={2}
                    onCardClick={document => {
                      navigate(`/public/help/${document?.slugUrl}`);
                    }}
                    totalItems={totalItems}
                    paginationState={paginationState}
                    data={documents}
                    onPaginationChange={newPaginationState => {
                      store.domain.list.set(storeName, {
                        paginationState: newPaginationState,
                        sortingState,
                        filters,
                      });
                    }}
                  />
                )}
              </Box>
            </PageSection>
          </Grid2>
          <Footer maxWidth="xl" />
        </Box>
      </Container>
    </Page>
  );
}

export default observer(PublicHelp);
